// The supplier hub (Supply → Suppliers & stock). Jacky, 16 October 2026: "this is central place for supplier
// related work". Until then this page read placeholder data that returns empty in production. Now: every
// supplier on one aligned list, and one page per supplier with its orders, the Jobs they feed, its terms,
// pricelists, the stock it supplies and how it performs. Orders and Inventory stay their own pages; the hub
// links into them instead of copying them.
//
// Performance is derived on read (services/api/src/suppliers.ts). A rate with nothing behind it shows "—",
// never 0 %: no delivery has been recorded yet, and a zero would accuse a supplier of what nobody recorded.
import {useState} from 'react';
import {Link, useParams, useSearchParams} from 'react-router-dom';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {cn} from '@aiw/ui';
import {Page, Panel} from '../shell/Page';
import {DataTable, TableSkeleton, Tag, Td} from '../components/aligned-table';
import {useT} from '../lib/i18n';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {day, money} from '../lib/format';
import {askEmma} from '../lib/communication-office';
import {OUTCOME_TONE, useProposer} from '../hooks/slice-b';
import {AccessoryMatching, PriceSearch, SupplierPriceList} from '../components/supplier-price-list';
import {SupplierPortalStock} from '../components/supplier-stock'; // carpet supplier B2B portals (26 Oct 2026)
import type {PortalRead} from '../lib/supplier-stock';
import {SupplierAccountsTab, SupplierAccountsView} from '../components/supplier-accounts'; // statements and Xero bills (4 Nov 2026)

export type SupplierPerformance = {
  window_days: number; orders: number; spend_ex_gst: number; confirmed_pct: number | null; on_time_pct: number | null;
  on_time_basis: number; avg_lead_days: number | null; open_orders: number; last_order_at: string | null; watch: boolean; watch_reason: string | null;
};
export type SupplierRecord = {
  id: string; name: string; short_name: string | null; category: string | null; ordering_email: string | null; sales_email: string | null;
  accounts_email: string | null; phone: string | null; warehouse_address: string | null; credit_terms: boolean | null;
  preferred_language: string | null; min_daily_charge: number | null; notes: string | null;
};
export type SupplierPromotion = {
  id: string; supplier_id: string; title: string; scope: string; scope_value: string | null; offer: string; offer_value: number | null;
  offer_unit: string | null; starts_on: string; ends_on: string; terms: string; source: string; source_ref: string | null;
  ended_at: string | null; ended_reason: string | null; state: 'current' | 'upcoming' | 'past';
};
export type SupplierListRow = SupplierRecord & {performance: SupplierPerformance; pricelist_date: string | null; stock_lines: number; current_promotions: number};
type OrderRow = {po: string; key: string; source: string; job_ref: string | null; job_name: string | null; status: string; sent_at: string | null; delivered_at: string | null; promised_date: string | null; total_ex_gst: number | null; group: string; confirmed: boolean};
type StockLine = {variant_id: string; location: string; location_label: string | null; variant_name: string | null; product_name: string | null; artemis_sku: string | null; product_uom: string | null; on_hand: number; committed: number; ordered: number; available: number; below_reorder: boolean};
export type SupplierContact = {id: string; name: string; role: string; email: string | null; phone: string | null; wechat_name: string | null; language: string; notes: string};
export type SupplierChannel = {id: string; kind: string; chat_name: string; room_id: string | null; note: string};
type TimelineEntry = {at: string; channel: 'email' | 'wechat' | 'record'; direction: 'in' | 'out' | 'note'; who: string | null; subject: string | null; text: string; ref: string | null};
type WechatChat = {chat_name: string; room_id: string | null; messages: number; last_at: string; linked_supplier_id: string | null};
export type SupplierDetailData = {
  supplier: SupplierRecord; performance: SupplierPerformance; contacts: SupplierContact[]; channels: SupplierChannel[]; promotions: SupplierPromotion[];
  terms: {lead_time_days: number; cut_off: string | null; min_order_aud: number; waste_pct: number; delivery_mode: string; contact_roles: Record<string, string>; notes: string; is_default: boolean};
  orders: OrderRow[];
  jobs: {job_ref: string; job_name: string | null; job_stage: string | null; orders: number; last_sent_at: string | null}[];
  pricelists: {source: 'pdf' | 'revision'; effective_date: string | null; status: string | null; name: string | null; received_at: string | null}[];
  stock: StockLine[];
};

async function get<T>(path: string): Promise<T> {
  const r = await fetchImpl(`${env.apiBase}${path}`, {headers: await authHeaders()});
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'Unavailable');
  return body.data as T;
}

const pctText = (v: number | null) => (v === null ? '—' : `${v} %`);
const n = (v: number) => v.toLocaleString('en-AU', {maximumFractionDigits: 2});
const shortDate = (iso: string | null) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00+10:00` : iso).toLocaleDateString('en-AU', {day: '2-digit', month: 'short', year: '2-digit', timeZone: 'Australia/Sydney'}) : '—');

export function Suppliers() {
  const {t} = useT();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const list = useQuery({queryKey: ['suppliers'], queryFn: () => get<SupplierListRow[]>('/api/suppliers'), refetchInterval: 120_000});
  const rows = list.data ?? [];
  const categories = [...new Set(rows.map((r) => r.category ?? '—'))].sort();
  const term = q.trim().toLowerCase();
  const shown = rows.filter((r) => (!category || (r.category ?? '—') === category)
    && (!term || [r.name, r.short_name, r.ordering_email, r.sales_email].some((v) => (v ?? '').toLowerCase().includes(term))));
  const watch = rows.filter((r) => r.performance.watch);
  // Suppliers | Statements & bills (4 Nov 2026). `?view=accounts` opens the statements, for EmmA's links.
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'accounts' ? 'accounts' : 'suppliers';

  return <Page kicker={t('sup.kicker')} title={t('sup.title')} lede={t('sup.lede')}>
    {watch.length > 0 && <div role="status" className="mb-3 rounded-lg border border-l-4 border-l-warning bg-warning/10 px-4 py-2 text-sm">
      <b>{t('sup.watchTitle', {count: watch.length})}</b> {watch.map((r) => r.short_name ?? r.name).join(' · ')}
    </div>}
    <div className="mb-3 flex flex-wrap gap-1 border-b" role="tablist">
      {(['suppliers', 'accounts'] as const).map((k) => (
        <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setParams(k === 'accounts' ? {view: 'accounts'} : {})}
          className={cn('-mb-px border-b-2 px-3 py-2 text-sm', view === k ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
          {t(k === 'accounts' ? 'sup.view.accounts' : 'sup.view.suppliers')}
        </button>
      ))}
    </div>
    {view === 'accounts' ? <SupplierAccountsView/> : <>
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <input className="h-8 w-64 rounded-md border bg-background px-2.5 text-sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('sup.search')} aria-label={t('sup.search')}/>
      <button type="button" onClick={() => setCategory(null)} className={cn('h-[26px] rounded-full border px-2.5 text-xs', category === null ? 'bg-primary text-primary-foreground' : 'bg-card')}>{t('sup.all')}</button>
      {categories.map((c) => (
        <button key={c} type="button" onClick={() => setCategory(category === c ? null : c)}
          className={cn('h-[26px] rounded-full border px-2.5 text-xs', category === c ? 'bg-primary text-primary-foreground' : 'bg-card')}>
          {c} <span className="tabular-nums opacity-70">{rows.filter((r) => (r.category ?? '—') === c).length}</span>
        </button>
      ))}
      <Link to="/purchase-orders" className="ml-auto text-xs text-primary underline">{t('sup.toOrders')}</Link>
      <Link to="/inventory" className="text-xs text-primary underline">{t('sup.toInventory')}</Link>
    </div>
    {list.isError && <p role="alert" className="mb-3 text-sm text-warning">{t('sup.unavailable', {message: (list.error as Error).message})}</p>}
    <PriceSearch/>
    <PromotionsNow/>
    <Panel title={t('sup.listTitle')} meta={t('sup.listMeta', {days: rows[0]?.performance.window_days ?? 90})}>
      <DataTable density="compact" cols={[
        {label: t('sup.col.supplier'), width: 240},
        {label: t('sup.col.category'), width: 120},
        {label: t('sup.col.open'), width: 70, num: true},
        {label: t('sup.col.orders'), width: 76, num: true},
        {label: t('sup.col.spend'), width: 110, num: true, hint: t('sup.exGstHint')},
        {label: t('sup.col.confirmed'), width: 92, num: true, hint: t('sup.confirmedHint')},
        {label: t('sup.col.onTime'), width: 80, num: true, hint: t('sup.onTimeHint')},
        {label: t('sup.col.pricelist'), width: 96},
        {label: t('sup.col.stock'), width: 64, num: true},
        {label: t('sup.col.last'), width: 96},
      ]}>
        {list.isPending && <TableSkeleton cols={10}/>}
        {shown.map((r) => (
          <tr key={r.id}>
            <Td title={r.name}><Link className="font-medium text-primary" to={`/suppliers/${encodeURIComponent(r.id)}`}>{r.name}</Link>
              {r.short_name && <span className="ml-1.5 font-mono text-micro text-muted-foreground">{r.short_name}</span>}
              {r.performance.watch && <span className="ml-1.5"><Tag tone="warn">{t('sup.watch')}</Tag></span>}
              {r.current_promotions > 0 && <span className="ml-1.5"><Tag tone="ok">{t('sup.promo')}</Tag></span>}</Td>
            <Td dim>{r.category ?? '—'}</Td>
            <Td num>{r.performance.open_orders || '—'}</Td>
            <Td num dim>{r.performance.orders || '—'}</Td>
            <Td num>{r.performance.spend_ex_gst ? money(r.performance.spend_ex_gst) : '—'}</Td>
            <Td num dim>{pctText(r.performance.confirmed_pct)}</Td>
            <Td num className={r.performance.watch ? 'text-warning' : undefined}>{pctText(r.performance.on_time_pct)}</Td>
            <Td dim>{shortDate(r.pricelist_date)}</Td>
            <Td num dim>{r.stock_lines || '—'}</Td>
            <Td dim>{shortDate(r.performance.last_order_at)}</Td>
          </tr>
        ))}
        {list.data && shown.length === 0 && <tr><Td colSpan={10} className="py-6 text-center" dim>{t('sup.none')}</Td></tr>}
      </DataTable>
      <p className="mt-2 text-xs text-muted-foreground">{t('sup.footnote')}</p>
    </Panel>
    </>}
  </Page>;
}

// `stock` is OUR warehouse for this supplier's items; `supplierStock` is THEIR shelf, read from their portal (shown only when one is read).
const TABS = ['orders', 'accounts', 'messages', 'jobs', 'stock', 'supplierStock', 'pricelists', 'accessories'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL = {orders: 'sup.tab.orders', accounts: 'sup.tab.accounts', messages: 'sup.tab.messages', jobs: 'sup.tab.jobs', stock: 'sup.tab.stock', supplierStock: 'sup.tab.supplierStock', pricelists: 'sup.tab.pricelists', accessories: 'sup.tab.accessories'} as const;

export function SupplierDetail() {
  const {t} = useT();
  const {id = ''} = useParams();
  // A link may name the tab (`?tab=supplierStock`) — EmmA's links and the live screen check use it.
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => (TABS as readonly string[]).includes(params.get('tab') ?? '') ? params.get('tab') as Tab : 'orders');
  const q = useQuery({queryKey: ['supplier', id], queryFn: () => get<SupplierDetailData>(`/api/suppliers/${encodeURIComponent(id)}`), enabled: !!id});
  const portal = useQuery({queryKey: ['supplier-portal', id], queryFn: () => get<PortalRead | null>(`/api/suppliers/${encodeURIComponent(id)}/portal`), enabled: !!id});
  const d = q.data;
  if (q.isError) return <Page kicker={t('sup.kicker')} title={t('sup.title')}><p role="alert" className="text-sm text-warning">{t('sup.unavailable', {message: (q.error as Error).message})}</p></Page>;
  if (!d) return <Page kicker={t('sup.kicker')} title={t('sup.loading')}><p role="status" className="text-sm text-muted-foreground">{t('sup.loading')}</p></Page>;
  const s = d.supplier, p = d.performance;
  const counts: Partial<Record<Tab, number>> = {orders: d.orders.length, jobs: d.jobs.length, stock: d.stock.length, pricelists: d.pricelists.length, ...(portal.data ? {supplierStock: portal.data.colours} : {})};
  const tabs = TABS.filter((k) => k !== 'supplierStock' || !!portal.data);

  return <Page kicker={t('sup.kicker')} title={s.name}
    lede={[s.short_name, s.category, s.credit_terms ? t('sup.creditTerms') : t('sup.noCreditTerms'), s.preferred_language === 'zh' ? t('sup.worksInChinese') : null].filter(Boolean).join(' · ')}
    actions={<>
      <button type="button" className="rounded-md border bg-card px-3 py-1.5 text-sm" onClick={() => askEmma(t('sup.askEmmaPrompt', {name: s.name, id: s.id}))}>{t('sup.askEmma')}</button>
      <Link to="/suppliers" className="text-sm text-primary underline">{t('sup.back')}</Link>
    </>}>
    {p.watch && <div role="status" className="mb-3 rounded-lg border border-l-4 border-l-warning bg-warning/10 px-4 py-2 text-sm">{t('sup.watchReason', {min: (p.watch_reason ?? '').split(':')[1] ?? ''})}</div>}

    <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-5">
      <Stat label={t('sup.stat.open')} value={String(p.open_orders)}/>
      <Stat label={t('sup.stat.spend', {days: p.window_days})} value={p.spend_ex_gst ? money(p.spend_ex_gst) : '—'} hint={t('sup.exGstHint')}/>
      <Stat label={t('sup.stat.confirmed')} value={pctText(p.confirmed_pct)} hint={t('sup.stat.ofOrders', {count: p.orders})}/>
      <Stat label={t('sup.stat.onTime')} value={pctText(p.on_time_pct)} hint={p.on_time_basis ? t('sup.stat.ofDelivered', {count: p.on_time_basis}) : t('sup.stat.noDeliveries')} warn={p.watch}/>
      <Stat label={t('sup.stat.lead')} value={p.avg_lead_days === null ? '—' : t('sup.days', {count: p.avg_lead_days})}/>
    </div>

    <div className="grid gap-4 lg:grid-cols-3">
      <Panel title={t('sup.contact')} className="lg:col-span-1">
        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-sm">
          <Field label={t('sup.f.ordering')} value={s.ordering_email} mono/>
          <Field label={t('sup.f.sales')} value={s.sales_email} mono/>
          <Field label={t('sup.f.accounts')} value={s.accounts_email} mono/>
          <Field label={t('sup.f.phone')} value={s.phone} mono/>
          <Field label={t('sup.f.warehouse')} value={s.warehouse_address}/>
          {s.notes && <Field label={t('sup.f.notes')} value={s.notes}/>}
        </dl>
        <p className="mt-2 text-xs text-muted-foreground">{t('sup.contactFootnote')}</p>
      </Panel>
      <Panel title={t('sup.terms')} meta={d.terms.is_default ? t('sup.termsDefault') : undefined} className="lg:col-span-2">
        <dl className="grid grid-cols-[130px_1fr] gap-x-3 gap-y-1.5 text-sm sm:grid-cols-[130px_1fr_130px_1fr]">
          <Field label={t('sup.f.lead')} value={t('sup.days', {count: d.terms.lead_time_days})}/>
          <Field label={t('sup.f.cutOff')} value={d.terms.cut_off}/>
          <Field label={t('sup.f.minOrder')} value={d.terms.min_order_aud ? money(d.terms.min_order_aud) : null}/>
          <Field label={t('sup.f.delivery')} value={d.terms.delivery_mode === 'pickup' ? t('sup.pickup') : t('sup.delivery')}/>
          <Field label={t('sup.f.waste')} value={`${d.terms.waste_pct} %`}/>
          {Object.entries(d.terms.contact_roles).map(([role, who]) => <Field key={role} label={role} value={who}/>)}
          {d.terms.notes && <Field label={t('sup.f.notes')} value={d.terms.notes}/>}
        </dl>
      </Panel>
    </div>

    <Promotions supplierId={s.id} promotions={d.promotions}/>

    <div className="mt-4 grid gap-4 lg:grid-cols-3">
      <People supplierId={s.id} contacts={d.contacts}/>
      <WechatChats supplierId={s.id} channels={d.channels}/>
    </div>

    <div className="mb-3 mt-5 flex flex-wrap gap-1 border-b" role="tablist">
      {tabs.map((k) => (
        <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
          className={cn('-mb-px border-b-2 px-3 py-2 text-sm', tab === k ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
          {t(TAB_LABEL[k])}{counts[k] !== undefined && <span className="ml-1.5 rounded-full bg-muted px-1.5 font-mono text-micro text-muted-foreground">{counts[k]}</span>}
        </button>
      ))}
    </div>

    {tab === 'orders' && <DataTable density="compact" cols={[
      {label: t('sup.col.order'), width: 190, mono: true}, {label: t('sup.col.job')}, {label: t('sup.col.status'), width: 120},
      {label: t('sup.col.sent'), width: 96}, {label: t('sup.col.promised'), width: 96}, {label: t('sup.col.total'), width: 110, num: true, hint: t('sup.exGstHint')},
    ]}>
      {d.orders.map((o) => (
        <tr key={o.key}>
          <Td mono><Link className="text-primary" to={`/purchase-orders/${encodeURIComponent(o.key)}`}>{o.po}</Link></Td>
          <Td title={o.job_name ?? ''}>{o.job_ref ? <Link className="text-primary" to={`/jobs/${o.job_ref}`}>{o.job_name ?? o.job_ref}</Link> : <span className="text-muted-foreground">{t('sup.stockOrder')}</span>}</Td>
          <Td><Tag tone={o.group === 'done' ? 'ok' : o.confirmed ? 'info' : undefined}>{o.status}</Tag></Td>
          <Td dim>{shortDate(o.sent_at)}</Td>
          <Td dim>{shortDate(o.delivered_at ?? o.promised_date)}</Td>
          <Td num>{money(o.total_ex_gst)}</Td>
        </tr>
      ))}
      {d.orders.length === 0 && <tr><Td colSpan={6} className="py-6 text-center" dim>{t('sup.noOrders')}</Td></tr>}
    </DataTable>}

    {tab === 'messages' && <Messages supplierId={s.id}/>}

    {tab === 'jobs' && <DataTable density="compact" cols={[{label: t('sup.col.job')}, {label: t('sup.col.stage'), width: 170}, {label: t('sup.col.orders'), width: 76, num: true}, {label: t('sup.col.last'), width: 96}]}>
      {d.jobs.map((j) => (
        <tr key={j.job_ref}>
          <Td><Link className="text-primary" to={`/jobs/${j.job_ref}`}>{j.job_name ?? j.job_ref}</Link> <span className="font-mono text-micro text-muted-foreground">{j.job_ref}</span></Td>
          <Td dim>{j.job_stage ?? '—'}</Td>
          <Td num>{j.orders}</Td>
          <Td dim>{shortDate(j.last_sent_at)}</Td>
        </tr>
      ))}
      {d.jobs.length === 0 && <tr><Td colSpan={4} className="py-6 text-center" dim>{t('sup.noJobs')}</Td></tr>}
    </DataTable>}

    {tab === 'stock' && <>
      <DataTable density="compact" cols={[
        {label: t('inv.col.sku'), width: 130, mono: true}, {label: t('inv.col.product')}, {label: t('inv.col.location'), width: 150},
        {label: t('inv.col.uom'), width: 56, mono: true}, {label: t('inv.col.onHand'), width: 86, num: true}, {label: t('inv.col.committed'), width: 96, num: true},
        {label: t('inv.col.ordered'), width: 84, num: true}, {label: t('inv.col.available'), width: 96, num: true},
      ]}>
        {d.stock.map((r) => (
          <tr key={`${r.variant_id}:${r.location}`}>
            <Td mono>{r.artemis_sku ?? '—'}</Td>
            <Td>{r.variant_name ?? r.product_name ?? '—'}</Td>
            <Td><Tag mono>{r.location_label ?? r.location}</Tag></Td>
            <Td mono dim>{r.product_uom ?? '—'}</Td>
            <Td num>{n(r.on_hand)}</Td>
            <Td num dim>{n(r.committed)}</Td>
            <Td num dim>{r.ordered ? n(r.ordered) : '—'}</Td>
            <Td num className={r.available < 0 || r.below_reorder ? 'text-warning' : undefined}>{n(r.available)}</Td>
          </tr>
        ))}
        {d.stock.length === 0 && <tr><Td colSpan={8} className="py-6 text-center" dim>{t('sup.noStock')}</Td></tr>}
      </DataTable>
      <p className="mt-2 text-xs text-muted-foreground"><Link className="text-primary underline" to="/inventory">{t('sup.toInventory')}</Link></p>
    </>}

    {tab === 'pricelists' && <SupplierPriceList supplierId={s.id} supplierName={s.name}/>}
    {tab === 'pricelists' && <h3 className="mb-2 mt-5 text-sm font-semibold">{t('sup.pl.history')}</h3>}
    {tab === 'pricelists' && <DataTable density="compact" cols={[{label: t('sup.col.effective'), width: 110}, {label: t('sup.col.document')}, {label: t('sup.col.kind'), width: 130}, {label: t('sup.col.received'), width: 110}]}>
      {d.pricelists.map((pl, i) => (
        <tr key={i}>
          <Td>{shortDate(pl.effective_date)}</Td>
          <Td dim title={pl.name ?? ''}>{pl.name ?? '—'}</Td>
          <Td dim>{pl.source === 'pdf' ? t('sup.pricelistPdf') : t('sup.priceRevision')}</Td>
          <Td dim>{day(pl.received_at)}</Td>
        </tr>
      ))}
      {d.pricelists.length === 0 && <tr><Td colSpan={4} className="py-6 text-center" dim>{t('sup.noPricelists')}</Td></tr>}
    </DataTable>}

    {tab === 'accessories' && <AccessoryMatching supplierId={s.id} supplierName={s.name}/>}
    {tab === 'accounts' && <SupplierAccountsTab supplierId={s.id}/>}
    {tab === 'supplierStock' && portal.data && <SupplierPortalStock supplierId={s.id} read={portal.data}/>}
  </Page>;
}

function Stat({label, value, hint, warn}: {label: string; value: string; hint?: string; warn?: boolean}) {
  return <div className="rounded-lg border bg-card px-3 py-2">
    <div className="text-micro uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={cn('text-lg font-semibold tabular-nums', warn && 'text-warning')}>{value}</div>
    {hint && <div className="truncate text-micro text-muted-foreground">{hint}</div>}
  </div>;
}

function Field({label, value, mono}: {label: string; value: string | null | undefined; mono?: boolean}) {
  return <>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className={cn('min-w-0 break-words', mono && 'font-mono text-xs', !value && 'text-muted-foreground')}>{value || '—'}</dd>
  </>;
}

const ROLES = ['rep', 'ordering', 'accounts', 'warehouse', 'owner', 'other'] as const;
const ROLE_LABEL = {rep: 'sup.role.rep', ordering: 'sup.role.ordering', accounts: 'sup.role.accounts', warehouse: 'sup.role.warehouse', owner: 'sup.role.owner', other: 'sup.role.other'} as const;
const roleLabel = (r: string) => (ROLE_LABEL as Record<string, (typeof ROLE_LABEL)[keyof typeof ROLE_LABEL]>)[r] ?? 'sup.role.other';
type ContactDraft = {id: string | null; name: string; role: string; email: string; phone: string; wechat_name: string; language: string; notes: string};
const EMPTY: ContactDraft = {id: null, name: '', role: 'rep', email: '', phone: '', wechat_name: '', language: 'en', notes: ''};

/** The people at a supplier — added, changed and archived through the backbone, like every write. */
function People({supplierId, contacts}: {supplierId: string; contacts: SupplierContact[]}) {
  const {t} = useT();
  const qc = useQueryClient();
  const {send, outcomes, busy} = useProposer();
  const [draft, setDraft] = useState<ContactDraft | null>(null);
  const done = () => qc.invalidateQueries({queryKey: ['supplier', supplierId]});
  const save = async () => {
    if (!draft) return;
    const o = await send('contact', 'save_supplier_contact', {id: draft.id, supplier_id: supplierId, name: draft.name, role: draft.role,
      email: draft.email.trim() || null, phone: draft.phone.trim() || null, wechat_name: draft.wechat_name.trim() || null, language: draft.language, notes: draft.notes}, t('sup.saved'));
    if (o.tone === 'ok') { setDraft(null); await done(); }
  };
  const archive = async (c: SupplierContact) => {
    const reason = window.prompt(t('sup.archiveWhy', {name: c.name}));
    if (!reason || reason.trim().length < 3) return;
    const o = await send(`archive:${c.id}`, 'archive_supplier_contact', {id: c.id, reason: reason.trim()}, t('sup.archived'));
    if (o.tone === 'ok') await done();
  };
  const field = (k: keyof ContactDraft, label: string, type = 'text') => (
    <label className="grid gap-0.5 text-xs text-muted-foreground">{label}
      <input type={type} className="h-8 rounded-md border bg-background px-2 text-sm text-foreground" value={draft?.[k] ?? ''} onChange={(e) => setDraft((d) => (d ? {...d, [k]: e.target.value} : d))}/>
    </label>
  );
  return <Panel title={t('sup.people')} meta={t('sup.peopleMeta')} className="lg:col-span-2">
    <DataTable density="compact" cols={[{label: t('sup.col.name'), width: 160}, {label: t('sup.col.role'), width: 100}, {label: t('sup.f.email'), width: 190, mono: true}, {label: t('sup.f.phone'), width: 110, mono: true}, {label: t('sup.f.wechat'), width: 110}, {label: '', width: 110}]}>
      {contacts.map((c) => (
        <tr key={c.id}>
          <Td title={c.notes}>{c.name}{c.language === 'zh' && <span className="ml-1.5 text-micro text-muted-foreground">中文</span>}</Td>
          <Td dim>{t(roleLabel(c.role))}</Td>
          <Td mono dim>{c.email ?? '—'}</Td>
          <Td mono dim>{c.phone ?? '—'}</Td>
          <Td dim>{c.wechat_name ?? '—'}</Td>
          <Td className="text-right">
            <button type="button" className="text-xs text-primary underline" onClick={() => setDraft({id: c.id, name: c.name, role: c.role, email: c.email ?? '', phone: c.phone ?? '', wechat_name: c.wechat_name ?? '', language: c.language, notes: c.notes})}>{t('sup.edit')}</button>
            <button type="button" className="ml-2 text-xs text-muted-foreground underline" disabled={busy[`archive:${c.id}`]} onClick={() => archive(c)}>{t('sup.archive')}</button>
          </Td>
        </tr>
      ))}
      {contacts.length === 0 && <tr><Td colSpan={6} className="py-4 text-center" dim>{t('sup.noPeople')}</Td></tr>}
    </DataTable>
    {draft ? <div className="mt-3 grid gap-2 rounded-lg border bg-muted/40 p-3 sm:grid-cols-3">
      {field('name', t('sup.col.name'))}
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.col.role')}
        <select className="h-8 rounded-md border bg-background px-2 text-sm text-foreground" value={draft.role} onChange={(e) => setDraft({...draft, role: e.target.value})}>
          {ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r])}</option>)}
        </select>
      </label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.f.language')}
        <select className="h-8 rounded-md border bg-background px-2 text-sm text-foreground" value={draft.language} onChange={(e) => setDraft({...draft, language: e.target.value})}>
          <option value="en">English</option><option value="zh">中文</option>
        </select>
      </label>
      {field('email', t('sup.f.email'), 'email')}
      {field('phone', t('sup.f.phone'), 'tel')}
      {field('wechat_name', t('sup.f.wechat'))}
      <div className="sm:col-span-3">{field('notes', t('sup.f.notes'))}</div>
      <div className="flex items-center gap-2 sm:col-span-3">
        <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={busy.contact || !draft.name.trim()} onClick={save}>{t('sup.save')}</button>
        <button type="button" className="text-sm text-muted-foreground underline" onClick={() => setDraft(null)}>{t('sup.cancel')}</button>
        {outcomes.contact && <span className={cn('text-xs', OUTCOME_TONE[outcomes.contact.tone])}>{outcomes.contact.text}</span>}
      </div>
    </div> : <button type="button" className="mt-2 text-sm text-primary underline" onClick={() => setDraft(EMPTY)}>{t('sup.addPerson')}</button>}
  </Panel>;
}

/** The WeChat chats that belong to this supplier: their messages show under Messages, and EmmA knows who a chat is with. */
function WechatChats({supplierId, channels}: {supplierId: string; channels: SupplierChannel[]}) {
  const {t} = useT();
  const qc = useQueryClient();
  const {send, outcomes, busy} = useProposer();
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState('');
  const [last, setLast] = useState<string | null>(null);
  const chats = useQuery({queryKey: ['wechat-chats'], queryFn: () => get<WechatChat[]>('/api/supplier-channels/wechat'), enabled: picking});
  const free = (chats.data ?? []).filter((c) => !c.linked_supplier_id);
  const done = async () => { await qc.invalidateQueries({queryKey: ['supplier', supplierId]}); await qc.invalidateQueries({queryKey: ['wechat-chats']}); await qc.invalidateQueries({queryKey: ['supplier-messages', supplierId]}); };
  const link = async () => {
    const chat = free.find((c) => c.chat_name === chosen);
    if (!chat) return;
    setLast('link');
    const o = await send('link', 'link_supplier_wechat', {supplier_id: supplierId, chat_name: chat.chat_name, room_id: chat.room_id, note: ''}, t('sup.linked'));
    if (o.tone === 'ok') { setPicking(false); setChosen(''); await done(); }
  };
  const unlink = async (c: SupplierChannel) => {
    if (!window.confirm(t('sup.unlinkConfirm', {chat: c.chat_name}))) return;
    setLast(`unlink:${c.id}`);
    const o = await send(`unlink:${c.id}`, 'unlink_supplier_wechat', {id: c.id, reason: 'Unlinked from the supplier page'}, t('sup.unlinked'));
    if (o.tone === 'ok') await done();
  };
  return <Panel title={t('sup.wechat')} meta={t('sup.wechatMeta')}>
    <ul className="grid gap-1.5 text-sm">
      {channels.map((c) => (
        <li key={c.id} className="flex items-center justify-between gap-2">
          <span className="min-w-0 truncate" title={c.room_id ?? ''}>{c.chat_name}</span>
          <button type="button" className="shrink-0 text-xs text-muted-foreground underline" disabled={busy[`unlink:${c.id}`]} onClick={() => unlink(c)}>{t('sup.unlink')}</button>
        </li>
      ))}
      {channels.length === 0 && <li className="text-muted-foreground">{t('sup.noWechat')}</li>}
    </ul>
    {picking ? <div className="mt-3 grid gap-2">
      <select className="h-8 rounded-md border bg-background px-2 text-sm" value={chosen} onChange={(e) => setChosen(e.target.value)} aria-label={t('sup.pickChat')}>
        <option value="">{chats.isPending ? t('sup.loading') : t('sup.pickChat')}</option>
        {free.map((c) => <option key={c.chat_name} value={c.chat_name}>{c.chat_name} · {t('sup.chatMessages', {count: c.messages})} · {shortDate(c.last_at)}</option>)}
      </select>
      <div className="flex items-center gap-2">
        <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={!chosen || busy.link} onClick={link}>{t('sup.link')}</button>
        <button type="button" className="text-sm text-muted-foreground underline" onClick={() => setPicking(false)}>{t('sup.cancel')}</button>
      </div>
      {chats.isError && <p role="alert" className="text-xs text-warning">{(chats.error as Error).message}</p>}
    </div> : <button type="button" className="mt-2 text-sm text-primary underline" onClick={() => setPicking(true)}>{t('sup.linkChat')}</button>}
    {last && outcomes[last] && <p className={cn('mt-1 text-xs', OUTCOME_TONE[outcomes[last]!.tone])}>{outcomes[last]!.text}</p>}
  </Panel>;
}

const CHANNEL_LABEL = {email: 'sup.ch.email', wechat: 'sup.ch.wechat', record: 'sup.ch.record'} as const;

/** One timeline: supplier email the PO Office read, what we drafted or sent, recorded evidence, and their WeChat chats. */
function Messages({supplierId}: {supplierId: string}) {
  const {t} = useT();
  const [only, setOnly] = useState<'all' | 'email' | 'wechat' | 'record'>('all');
  const q = useQuery({queryKey: ['supplier-messages', supplierId], queryFn: () => get<TimelineEntry[]>(`/api/suppliers/${encodeURIComponent(supplierId)}/communication`), refetchInterval: 60_000});
  const rows = (q.data ?? []).filter((r) => only === 'all' || r.channel === only);
  return <div>
    <div className="mb-2 flex flex-wrap gap-1">
      {(['all', 'email', 'wechat', 'record'] as const).map((k) => (
        <button key={k} type="button" onClick={() => setOnly(k)} className={cn('h-[26px] rounded-full border px-2.5 text-xs', only === k ? 'bg-primary text-primary-foreground' : 'bg-card')}>
          {k === 'all' ? t('sup.all') : t(CHANNEL_LABEL[k])}
        </button>
      ))}
    </div>
    {q.isError && <p role="alert" className="mb-2 text-sm text-warning">{t('sup.unavailable', {message: (q.error as Error).message})}</p>}
    {q.isPending && <p role="status" className="text-sm text-muted-foreground">{t('sup.loading')}</p>}
    <ol className="grid gap-1.5">
      {rows.map((r, i) => (
        <li key={i} className={cn('rounded-lg border bg-card px-3 py-2 text-sm', r.direction === 'out' && 'border-l-4 border-l-primary', r.channel === 'record' && 'bg-muted/40')}>
          <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
            <Tag tone={r.channel === 'wechat' ? 'ok' : r.channel === 'email' ? 'info' : undefined}>{t(CHANNEL_LABEL[r.channel])}</Tag>
            <span>{new Date(r.at).toLocaleString('en-AU', {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney'})}</span>
            <span>{r.direction === 'out' ? t('sup.dir.out') : r.direction === 'in' ? t('sup.dir.in') : t('sup.dir.note')}</span>
            {r.who && <span className="font-mono">{r.who}</span>}
            {r.ref && <span className="font-mono">{r.ref}</span>}
          </div>
          {r.subject && <div className="font-medium">{r.subject}</div>}
          {r.text && <p className="whitespace-pre-wrap break-words text-foreground/90">{r.text}</p>}
        </li>
      ))}
      {q.data && rows.length === 0 && <li className="py-4 text-center text-sm text-muted-foreground">{t('sup.noMessages')}</li>}
    </ol>
    <p className="mt-2 text-xs text-muted-foreground">{t('sup.messagesFootnote')}</p>
  </div>;
}

/** "10% off", "$42.50 EX GST / m2", "free freight" — the offer in words. Exported for the order page. */
export function offerWords(p: Pick<SupplierPromotion, 'offer' | 'offer_value' | 'offer_unit'>, t: ReturnType<typeof useT>['t']): string {
  if (p.offer === 'percent_off') return t('sup.promo.percentOff', {value: String(p.offer_value ?? '')});
  if (p.offer === 'price') return `${money(p.offer_value)} ${t('sup.exGstHint')}${p.offer_unit ? ` / ${p.offer_unit}` : ''}`;
  return t(OFFER_LABEL[p.offer as keyof typeof OFFER_LABEL] ?? 'sup.offer.other');
}
const OFFER_LABEL = {percent_off: 'sup.offer.percent_off', price: 'sup.offer.price', free_freight: 'sup.offer.free_freight', bonus: 'sup.offer.bonus', other: 'sup.offer.other'} as const;
const SCOPE_LABEL = {all: 'sup.scope.all', category: 'sup.scope.category', collection: 'sup.scope.collection', product: 'sup.scope.product', variant: 'sup.scope.variant'} as const;
const SOURCE_LABEL = {email: 'sup.ch.email', wechat: 'sup.ch.wechat', pdf: 'sup.src.pdf', rep: 'sup.src.rep', other: 'sup.role.other'} as const;
export const coversWords = (p: Pick<SupplierPromotion, 'scope' | 'scope_value'>, t: ReturnType<typeof useT>['t']) =>
  p.scope === 'all' ? t('sup.scope.all') : `${t(SCOPE_LABEL[p.scope as keyof typeof SCOPE_LABEL] ?? 'sup.scope.all')}: ${p.scope_value ?? ''}`;

/** What suppliers are offering today, across all of them — above the list. */
function PromotionsNow() {
  const {t} = useT();
  const q = useQuery({queryKey: ['supplier-promotions'], queryFn: () => get<(SupplierPromotion & {supplier_id: string})[]>('/api/supplier-promotions')});
  const rows = q.data ?? [];
  if (!rows.length) return null;
  return <div role="status" className="mb-3 rounded-lg border border-l-4 border-l-success bg-success/10 px-4 py-2 text-sm">
    <b>{t('sup.promoNow', {count: rows.length})}</b>
    <ul className="mt-1 grid gap-0.5">
      {rows.slice(0, 6).map((p) => <li key={p.id}>
        <Link className="text-primary" to={`/suppliers/${encodeURIComponent(p.supplier_id)}`}>{p.title}</Link>
        <span className="text-muted-foreground"> · {coversWords(p, t)} · {offerWords(p, t)} · {t('sup.promo.until', {day: shortDate(p.ends_on)})}</span>
      </li>)}
    </ul>
  </div>;
}

type PromoDraft = {id: string | null; title: string; scope: string; scope_value: string; offer: string; offer_value: string; offer_unit: string; starts_on: string; ends_on: string; terms: string; source: string; source_ref: string};

/** The supplier's promotions: current and upcoming first, the last year's past ones folded. Information only. */
function Promotions({supplierId, promotions}: {supplierId: string; promotions: SupplierPromotion[]}) {
  const {t} = useT();
  const qc = useQueryClient();
  const {send, outcomes, busy} = useProposer();
  const today = new Intl.DateTimeFormat('en-CA', {timeZone: 'Australia/Sydney'}).format(new Date());
  const blank: PromoDraft = {id: null, title: '', scope: 'all', scope_value: '', offer: 'percent_off', offer_value: '', offer_unit: '', starts_on: today, ends_on: today, terms: '', source: 'email', source_ref: ''};
  const [draft, setDraft] = useState<PromoDraft | null>(null);
  const live = promotions.filter((p) => p.state !== 'past'), past = promotions.filter((p) => p.state === 'past');
  const valued = draft && ['percent_off', 'price'].includes(draft.offer);
  const done = async () => { await qc.invalidateQueries({queryKey: ['supplier', supplierId]}); await qc.invalidateQueries({queryKey: ['supplier-promotions']}); await qc.invalidateQueries({queryKey: ['suppliers']}); };
  const save = async () => {
    if (!draft) return;
    const o = await send('promo', 'record_supplier_promotion', {id: draft.id, supplier_id: supplierId, title: draft.title, scope: draft.scope, scope_value: draft.scope === 'all' ? null : draft.scope_value.trim() || null,
      offer: draft.offer, offer_value: valued ? Number(draft.offer_value) : null, offer_unit: draft.offer === 'price' && draft.offer_unit.trim() ? draft.offer_unit.trim() : null,
      starts_on: draft.starts_on, ends_on: draft.ends_on, terms: draft.terms, source: draft.source, source_ref: draft.source_ref.trim() || null}, t('sup.saved'));
    if (o.tone === 'ok') { setDraft(null); await done(); }
  };
  const end = async (p: SupplierPromotion) => {
    const reason = window.prompt(t('sup.promo.endWhy', {title: p.title}));
    if (!reason || reason.trim().length < 3) return;
    const o = await send(`end:${p.id}`, 'end_supplier_promotion', {id: p.id, reason: reason.trim()}, t('sup.promo.ended'));
    if (o.tone === 'ok') await done();
  };
  const input = 'h-8 rounded-md border bg-background px-2 text-sm text-foreground';
  const row = (p: SupplierPromotion) => <tr key={p.id}>
    <Td title={p.terms}>{p.title}{p.state === 'upcoming' && <span className="ml-1.5"><Tag>{t('sup.promo.upcoming')}</Tag></span>}</Td>
    <Td dim>{coversWords(p, t)}</Td>
    <Td>{offerWords(p, t)}</Td>
    <Td dim>{shortDate(p.starts_on)} – {shortDate(p.ends_on)}</Td>
    <Td dim title={p.source_ref ?? ''}>{t(SOURCE_LABEL[p.source as keyof typeof SOURCE_LABEL] ?? 'sup.role.other')}</Td>
    <Td className="text-right">{p.state !== 'past' && <>
      <button type="button" className="text-xs text-primary underline" onClick={() => setDraft({id: p.id, title: p.title, scope: p.scope, scope_value: p.scope_value ?? '', offer: p.offer, offer_value: p.offer_value == null ? '' : String(p.offer_value), offer_unit: p.offer_unit ?? '', starts_on: p.starts_on, ends_on: p.ends_on, terms: p.terms, source: p.source, source_ref: p.source_ref ?? ''})}>{t('sup.edit')}</button>
      <button type="button" className="ml-2 text-xs text-muted-foreground underline" disabled={busy[`end:${p.id}`]} onClick={() => end(p)}>{t('sup.promo.end')}</button>
    </>}{p.state === 'past' && p.ended_reason && <span className="text-xs text-muted-foreground">{p.ended_reason}</span>}</Td>
  </tr>;
  const cols = [{label: t('sup.promo.title')}, {label: t('sup.promo.covers'), width: 180}, {label: t('sup.promo.offer'), width: 150}, {label: t('sup.promo.dates'), width: 170}, {label: t('sup.promo.source'), width: 90}, {label: '', width: 110}];
  return <Panel title={t('sup.promos')} meta={t('sup.promosMeta')} className="mt-4">
    <DataTable density="compact" cols={cols}>
      {live.map(row)}
      {live.length === 0 && <tr><Td colSpan={6} className="py-4 text-center" dim>{t('sup.promo.none')}</Td></tr>}
    </DataTable>
    {past.length > 0 && <details className="mt-2 text-sm"><summary>{t('sup.promo.past', {count: past.length})}</summary>
      <DataTable density="compact" cols={cols} className="mt-2">{past.map(row)}</DataTable></details>}
    {draft ? <div className="mt-3 grid gap-2 rounded-lg border bg-muted/40 p-3 sm:grid-cols-4">
      <label className="grid gap-0.5 text-xs text-muted-foreground sm:col-span-2">{t('sup.promo.title')}<input className={input} value={draft.title} onChange={(e) => setDraft({...draft, title: e.target.value})} placeholder={t('sup.promo.titlePlaceholder')}/></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.promo.covers')}
        <select className={input} value={draft.scope} onChange={(e) => setDraft({...draft, scope: e.target.value})}>{Object.entries(SCOPE_LABEL).map(([k, id]) => <option key={k} value={k}>{t(id)}</option>)}</select></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.promo.which')}<input className={input} disabled={draft.scope === 'all'} value={draft.scope === 'all' ? '' : draft.scope_value} onChange={(e) => setDraft({...draft, scope_value: e.target.value})}/></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.promo.offer')}
        <select className={input} value={draft.offer} onChange={(e) => setDraft({...draft, offer: e.target.value})}>{Object.entries(OFFER_LABEL).map(([k, id]) => <option key={k} value={k}>{t(id)}</option>)}</select></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{draft.offer === 'price' ? t('sup.promo.priceExGst') : t('sup.promo.percent')}<input className={input} type="number" min="0" step="any" disabled={!valued} value={valued ? draft.offer_value : ''} onChange={(e) => setDraft({...draft, offer_value: e.target.value})}/></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.promo.unit')}<input className={input} disabled={draft.offer !== 'price'} value={draft.offer === 'price' ? draft.offer_unit : ''} placeholder="m2" onChange={(e) => setDraft({...draft, offer_unit: e.target.value})}/></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.promo.source')}
        <select className={input} value={draft.source} onChange={(e) => setDraft({...draft, source: e.target.value})}>{Object.entries(SOURCE_LABEL).map(([k, id]) => <option key={k} value={k}>{t(id)}</option>)}</select></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.promo.from')}<input className={input} type="date" value={draft.starts_on} onChange={(e) => setDraft({...draft, starts_on: e.target.value})}/></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground">{t('sup.promo.to')}<input className={input} type="date" min={draft.starts_on} value={draft.ends_on} onChange={(e) => setDraft({...draft, ends_on: e.target.value})}/></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground sm:col-span-2">{t('sup.promo.sourceRef')}<input className={input} value={draft.source_ref} onChange={(e) => setDraft({...draft, source_ref: e.target.value})}/></label>
      <label className="grid gap-0.5 text-xs text-muted-foreground sm:col-span-4">{t('sup.promo.terms')}<input className={input} value={draft.terms} onChange={(e) => setDraft({...draft, terms: e.target.value})}/></label>
      <div className="flex items-center gap-2 sm:col-span-4">
        <button type="button" className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={busy.promo || draft.title.trim().length < 3 || (valued && !(Number(draft.offer_value) > 0)) || (draft.scope !== 'all' && !draft.scope_value.trim())} onClick={save}>{t('sup.save')}</button>
        <button type="button" className="text-sm text-muted-foreground underline" onClick={() => setDraft(null)}>{t('sup.cancel')}</button>
        {outcomes.promo && <span className={cn('text-xs', OUTCOME_TONE[outcomes.promo.tone])}>{outcomes.promo.text}</span>}
      </div>
    </div> : <button type="button" className="mt-2 text-sm text-primary underline" onClick={() => setDraft(blank)}>{t('sup.promo.add')}</button>}
    <p className="mt-2 text-xs text-muted-foreground">{t('sup.promo.footnote')}</p>
  </Panel>;
}
